import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useForm, useFormState, useWatch } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import { RMSButton } from 'features/shared/components/buttons';
import { useAddressDuplicates } from 'features/user/hooks/useAddressDuplicates';
import AddressDuplicateDialog from './AddressDuplicateDialog';
import { ADDRESS_DUPLICATE_FIELD_LABELS } from 'features/user/constants/addressDuplicateDialogConstants';
import AddIcon from '@material-ui/icons/Add';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { RMSTextField, RMSSelect, RMSCheckbox } from 'features/shared/components/form-controls';
import { SectionCard } from 'features/shared/components/cards';
import { executeApiAction } from 'features/shared/utility/apiHelper';
import CreateFormResolver from 'features/shared/utility/createFormResolver';
import { notify } from 'reducers/NotifierReducer';
import { getDictionarySelectProps } from 'features/shared/utility/dictionarySelectHelper';
import {
  FORM_DEFAULT_VALUES,
  ESN_SELECT_PROPS,
} from 'features/user/constants/addAddressFormConstants';
import useUserDictionaryOptions from 'features/user/hooks/useUserDictionaryOptions';
import CityManagementDialog from 'features/user/components/common/CityManagementDialog';
import addAddressSchema from 'features/user/schema/addAddressSchema';
import { createAddress, getAddressDetails } from 'features/user/services/addressService';
import {
  buildFullAddressText,
  buildPostal,
  parsePostcode,
  buildAddressCopyValues,
} from 'features/user/utility/addAddressHelper';
import { NON_DIGITS_REGEX } from '../../../shared/constants/regex';

const AddressDetailsForm = forwardRef(
  ({ ptsAddressID, copyFromAddressId, onStateChange, onSaved }, ref) => {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user?.userData?.user);
    const cityDictPerms = useSelector((state) => state.permissions?.dictionary?.codeCities);
    const addressPerms = usePermissions(PERMISSION_RESOURCES.ADDRESS);
    const canSaveAddress = ptsAddressID ? addressPerms.Edit : addressPerms.Create;

    const methods = useForm({
      resolver: CreateFormResolver(addAddressSchema),
      mode: 'onSubmit',
      defaultValues: FORM_DEFAULT_VALUES,
      disabled: !canSaveAddress,
    });

    const showCityAddIcon = Boolean(
      cityDictPerms?.Create || cityDictPerms?.Edit || cityDictPerms?.Delete
    );
    const { control } = methods;
    const fullAddressText = useWatch({ control, name: 'FullAddressText' });
    const [created, createdBy, updated, updatedBy] = useWatch({
      control,
      name: ['Created', 'CreatedBy', 'Updated', 'UpdatedBy'],
    });
    const { duplicates, duplicateParams } = useAddressDuplicates(control, ptsAddressID);
    const [duplicateListOpen, setDuplicateListOpen] = useState(false);
    const hasDuplicates = duplicates.length > 0;
    const { isDirty } = useFormState({ control });
    const dirtyRef = useRef(isDirty);
    dirtyRef.current = isDirty;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [cityManagementDialogOpen, setCityManagementDialogOpen] = useState(false);

    const {
      streetDirectionOptions,
      streetTypeOptions,
      cityOptions,
      stateOptions,
      countyOptions,
      countryOptions,
      unitTypeOptions,
      addressCategoryOptions,
      floorTypeOptions,
      bldgTypeOptions,
      esnOptions,
    } = useUserDictionaryOptions();

    const cityDisplayOptions = cityOptions.map((city) => ({
      ...city,
      DisplayLabel: city.State ? `${city.Description} - ${city.State}` : city.Description,
    }));
    const handleCityCreated = useCallback(
      (city) => {
        methods.setValue('ptsCityID', city?.ptsCityID ?? null, {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
      [methods]
    );

    const loadAddress = useCallback(async () => {
      const sourceId = ptsAddressID || copyFromAddressId;
      if (!sourceId) {
        methods.reset(FORM_DEFAULT_VALUES);
        return;
      }

      await executeApiAction({
        setLoading,
        dispatch,
        action: () => getAddressDetails(sourceId),
        onSuccess: (address) => {
          if (!address) return;
          const values = {
            ...FORM_DEFAULT_VALUES,
            ...address,
            Postal: buildPostal(address.PostalCode, address.PostalCodeExtension),
            Landmark: address.LandmarkText || '',
            CampusResidential: Boolean(address.IsCampusResidential),
            CampusNonResidential: Boolean(address.IsCampusNonResidential),
            NonCampus: Boolean(address.IsNonCampus),
            Public: Boolean(address.IsPublic),
          };
          if (!ptsAddressID && copyFromAddressId) {
            methods.reset(buildAddressCopyValues(values, FORM_DEFAULT_VALUES), {
              keepDefaultValues: true,
            });
          } else {
            methods.reset(values);
          }
        },
      });
    }, [dispatch, methods, ptsAddressID, copyFromAddressId]);

    const onSubmit = useCallback(
      async (data) => {
        const { PostalCode, PostalCodeExtension } = parsePostcode(data.Postal);
        const city = cityOptions.find((option) => option.Code === data.ptsCityID);
        const postalText = PostalCode ? buildPostal(PostalCode, PostalCodeExtension) : '';
        const fullAddress = buildFullAddressText(
          { ...data, Postal: postalText },
          city?.Description
        );
        const {
          Landmark,
          CampusResidential,
          CampusNonResidential,
          NonCampus,
          Public,
          ...rest
        } = data;
        delete rest.CleryLocationRadio;
        const payload = {
          ...rest,
          ptsAddressID: ptsAddressID || data.ptsAddressID || null,
          PostalCode: PostalCode || null,
          PostalCodeExtension: PostalCodeExtension || null,
          LandmarkText: Landmark || null,
          FullAddressText: fullAddress,
          IsCampusResidential: Boolean(CampusResidential),
          IsCampusNonResidential: Boolean(CampusNonResidential),
          IsNonCampus: Boolean(NonCampus),
          IsPublic: Boolean(Public),
          CurrentUser: currentUser?.Username,
        };

        await executeApiAction({
          setLoading: setSubmitting,
          dispatch,
          action: async () => {
            const saved = await createAddress(payload);
            const savedId = saved?.ptsAddressID || saved?.id || payload.ptsAddressID;
            if (!savedId) return saved;
            const refreshed = await getAddressDetails(savedId);
            return { ...saved, ...refreshed, ptsAddressID: savedId };
          },
          onSuccess: (saved) => {
            methods.reset({
              ...data,
              ...(saved || {}),
              ptsAddressID: saved?.ptsAddressID || payload.ptsAddressID,
              FullAddressText: fullAddress,
            });
            dispatch(notify('Address saved successfully', 'success'));
            onSaved?.({ ...payload, ...(saved || {}), FullAddressText: fullAddress });
          },
        });
      },
      [cityOptions, currentUser, dispatch, methods, onSaved, ptsAddressID]
    );

    const handleSave = methods.handleSubmit(onSubmit);

    useImperativeHandle(
      ref,
      () => ({
        isDirty: () => dirtyRef.current,
        save: () => handleSave(),
      }),
      [handleSave]
    );

    const handlePostalChange = useCallback(
      (value) => {
        const digits = String(value ?? '')
          .replace(NON_DIGITS_REGEX, '')
          .slice(0, 9);

        const formattedPostal =
          digits.length > 5 ? `${digits.slice(0, 5)} - ${digits.slice(5)}` : digits;

        methods.setValue('Postal', formattedPostal, {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
      [methods]
    );

    useEffect(() => {
      loadAddress();
    }, [loadAddress]);

    useEffect(() => {
      onStateChange?.({
        isDirty,
        submitting: submitting || loading,
        canSave: canSaveAddress,
        fullAddressText,
        recordHistory: {
          Created: created,
          CreatedBy: createdBy,
          Updated: updated,
          UpdatedBy: updatedBy,
        },
      });
    }, [
      isDirty,
      submitting,
      loading,
      canSaveAddress,
      fullAddressText,
      created,
      createdBy,
      updated,
      updatedBy,
      onStateChange,
    ]);

    const field = (name, label, xs = 4, props = {}) => (
      <Grid item xs={12} sm={6} md={xs}>
        <RMSTextField name={name} label={label} {...props} />
      </Grid>
    );

    const select = (name, label, options, xs = 4, props = getDictionarySelectProps()) => (
      <Grid item xs={12} sm={6} md={xs}>
        <RMSSelect name={name} label={label} options={options} {...props} />
      </Grid>
    );

    return (
      <>
        <FormProvider {...methods}>
          <div className="address-details-form">
            {hasDuplicates && (
              <div className="address-details-form__duplicate-notice" role="status">
                <div className="address-details-form__duplicate-message">
                  <InfoOutlinedIcon fontSize="small" />
                  <div>
                    <span>
                      {duplicates.length} matching{' '}
                      {duplicates.length === 1 ? 'address' : 'addresses'} found. Review before
                      saving.
                    </span>
                    <div className="address-details-form__duplicate-fields">
                      Checked fields:{' '}
                      {duplicateParams
                        .map((key) => ADDRESS_DUPLICATE_FIELD_LABELS[key] || key)
                        .join(', ')}
                    </div>
                  </div>
                </div>
                <RMSButton
                  label="Show Duplicates"
                  className="address-details-form__duplicate-button"
                  color="default"
                  size="small"
                  variant="outlined"
                  onClick={() => setDuplicateListOpen(true)}
                />
              </div>
            )}
            <div className="address-details-form__columns">
              <div className="address-details-form__column">
                <SectionCard label="Address Details">
                  <Grid container spacing={2}>
                    {field('AddressNumberPrefix', 'Prefix', 3)}
                    {field('AddressNumber', 'Address Number', 3, { type: 'number' })}
                    {field('AddressNumberSuffix', 'Suffix', 3)}
                    {select('PreDirection', 'Pre Direction', streetDirectionOptions, 3)}
                    {field('StreetName', 'Street Name')}
                    {select('StreetType', 'Street Type', streetTypeOptions)}
                    {select('PostDirection', 'Post Direction', streetDirectionOptions)}
                    {select('State', 'State', stateOptions, 4)}
                    {select('ptsCityID', 'City', cityDisplayOptions, 4, {
                      ...getDictionarySelectProps(),
                      optionValue: 'Code',
                      optionLabel: 'DisplayLabel',
                      showLabelOnly: true,
                      clearable: true,
                      prefixIcon: showCityAddIcon ? <AddIcon fontSize="small" /> : undefined,
                      onPrefixIconClick: showCityAddIcon
                        ? () => setCityManagementDialogOpen(true)
                        : undefined,
                    })}
                    {field('Postal', 'Postal Code - Ext.', 4, {
                      type: 'postcode',
                      onChange: handlePostalChange,
                      inputProps: {
                        maxLength: 12,
                      },
                    })}
                    {select('County', 'County', countyOptions, 4)}
                    {select('Country', 'Country', countryOptions, 4)}
                    {select('AddressCategory', 'Address Category', addressCategoryOptions, 4)}
                  </Grid>
                </SectionCard>

                <SectionCard label="Notes">
                  <RMSTextField name="Notes" label="Notes" multiline rows={3} />
                </SectionCard>

                <SectionCard label="Location">
                  <Grid container spacing={2}>
                    {field('CrossStreet', 'Cross Street', 3)}
                    {field('Highway', 'Highway', 3)}
                    {select('ESN', 'ESN', esnOptions, 3, ESN_SELECT_PROPS)}
                    {field('Landmark', 'Landmark Text', 3)}
                    {field('SurroundingArea', 'Surrounding Area', 3)}
                    {field('Area', 'Area', 3)}
                    {field('Description', 'Description', 3, { multiline: true, rows: 3 })}
                    {field('RangeDescription', 'Range Description', 3)}
                    {field('MapLocation', 'Map Location', 3)}
                    {field('RelativeLocation', 'Relative Location', 3)}
                  </Grid>
                </SectionCard>
              </div>

              <div className="address-details-form__column">
                <SectionCard label="Identifiers">
                  <Grid container spacing={2}>
                    {select('UnitType', 'Unit Type', unitTypeOptions)}
                    {field('UnitIdentifier', 'Unit Identifier')}
                    {select('FloorType', 'Floor Type', floorTypeOptions)}
                    {field('FloorIdentifier', 'Floor Identifier')}
                    {select('BldgType', 'Building Type', bldgTypeOptions)}
                    {field('BldgIdentifier', 'Building Identifier')}
                  </Grid>
                </SectionCard>

                <SectionCard label="Flags">
                  <div className="address-details-form__checks">
                    <RMSCheckbox name="ShowInEventLookup" label="Show In Event Lookup" />
                    <RMSCheckbox name="SexOffender" label="Sex Offender" />
                    <RMSCheckbox name="Parolee" label="Parolee" />
                    <RMSCheckbox name="Probation" label="Probation" />
                    <RMSCheckbox name="NonValidatedAddress" label="Non Validated Address" />
                  </div>
                </SectionCard>

                <SectionCard label="Clery Location">
                  <div className="address-details-form__checks">
                    <RMSCheckbox name="CampusResidential" label="Campus Residential" />
                    <RMSCheckbox name="CampusNonResidential" label="Campus Non-Residential" />
                    <RMSCheckbox name="NonCampus" label="Non-Campus" />
                    <RMSCheckbox name="Public" label="Public" />
                  </div>
                </SectionCard>

                <SectionCard label="Geographical Details">
                  <Grid container spacing={2}>
                    {field('AltitudeMeasure', 'Altitude Measure')}
                    {field('DepthMeasure', 'Depth Measure')}
                    {field('GeographicElevation', 'Geographic Elevation')}
                    {field('TwoDGeographicCoordinate', 'Two D Geographic Coordinate')}
                    {field('UTMCoordinate', 'UTM Coordinate')}
                    {field('MGRSCoordinate', 'MGRS Coordinate')}
                  </Grid>
                </SectionCard>
              </div>
            </div>

            <SectionCard label="Record Fields">
              <Grid container spacing={2}>
                {field('FullAddressText', 'Full Address Text', 2)}
                <Grid item xs={12} sm={6} md={2}>
                  <RMSTextField name="ptsAddressID" label="Address ID" disabled />
                </Grid>
                {field('Locale', 'Locale', 2)}
              </Grid>
            </SectionCard>
          </div>
        </FormProvider>
        <AddressDuplicateDialog
          open={duplicateListOpen && hasDuplicates}
          onClose={() => setDuplicateListOpen(false)}
          rows={duplicates}
        />
        {cityManagementDialogOpen && (
          <CityManagementDialog
            open={cityManagementDialogOpen}
            onClose={() => setCityManagementDialogOpen(false)}
            onCityCreated={handleCityCreated}
          />
        )}
      </>
    );
  }
);

export default AddressDetailsForm;
